import { computed, nextTick } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { navigateTo, useRoute } from '#imports'
import { matchesExpectedPath } from '~/onboarding/path'
import { hasTourCapabilities } from '~/onboarding/tourSelection'
import { listAccessibleTrainings } from '~/onboarding/trainingItems'
import { ONBOARDING_TOURS } from '~/onboarding/catalog'
import type { OnboardingStateMap, OnboardingTourDefinition } from '~/onboarding/types'

type LaunchTrainingMode = 'do' | 'redo'

type TrainingCenterDeps = {
  roles: Ref<string[]>
  launchBlockerReason: ComputedRef<string | null>
  resolveOnboardingStateMapForChecks: () => OnboardingStateMap
  toastInfo: (title: string, message: string) => void
  stopCurrentTour: () => void
  startTour: (tour: OnboardingTourDefinition) => Promise<boolean>
  resetSingleTourProgress: (tourId: string) => void
  disabledTourIds: ComputedRef<ReadonlySet<string>>
  isTourDisabled: (tourId: string) => boolean
}

/**
 * Expose la liste des formations disponibles ("Mes formations") et la logique de lancement
 * manuel d’un tour depuis le centre de formation (bouton "Faire" / "Refaire").
 */
export function useOnboardingTrainingCenter(deps: TrainingCenterDeps) {
  const route = useRoute()

  const availableTrainings = computed(() =>
    listAccessibleTrainings(
      deps.roles.value,
      deps.resolveOnboardingStateMapForChecks(),
      deps.launchBlockerReason.value,
      deps.disabledTourIds.value
    )
  )

  const trainingsSummary = computed(() => {
    const list = availableTrainings.value
    return {
      doneCount: list.filter(item => item.isDone).length,
      totalCount: list.length,
      redoCount: list.filter(item => item.needsRedo).length,
    }
  })

  async function startTrainingById(tourId: string, mode: LaunchTrainingMode): Promise<boolean> {
    const blocker = deps.launchBlockerReason.value
    if (blocker !== null) {
      deps.toastInfo('Formation indisponible', blocker)
      return false
    }
    const tour = ONBOARDING_TOURS.find(candidateTour => candidateTour.id === tourId)
    if (tour === undefined || !hasTourCapabilities(tour, deps.roles.value)) {
      deps.toastInfo('Formation indisponible', 'Vous n’avez pas les droits nécessaires pour lancer cette formation.')
      return false
    }
    if (deps.isTourDisabled(tourId)) {
      deps.toastInfo('Formation indisponible', 'Ce guide est désactivé.')
      return false
    }
    const entryPath = tour.entryPath
    deps.stopCurrentTour()
    if (mode === 'redo') {
      deps.resetSingleTourProgress(tourId)
    }
    if (!matchesExpectedPath(route.path, entryPath)) {
      await navigateTo({ path: entryPath })
      await nextTick()
    }
    return deps.startTour(tour)
  }

  return {
    availableTrainings,
    trainingsSummary,
    startTrainingById,
  }
}
